import { inject, Injectable } from '@angular/core';
import { ROUTES } from '@core/constants';
import {
  CreateQuotePayload,
  IResponseHttp,
  PublicQuoteDetail,
  ServiceCategoryOption,
  QuoteApprovalDecision,
  QuoteDraft,
  QuoteItem,
  QuoteListItem,
  QuoteListStatus,
  QuoteSendResult,
  QuoteUpdateResult,
  ResponseCreateServiceQuote,
  EvidenceType,
} from '@core/models';
import {
  getFileExtension,
  readNullableBoolean,
  readNullableStringByKeys,
  readNumber,
  readString,
  toObject,
  unwrapResponse,
} from '@core/utils';
import { ApiService } from '@core/services/api/api.service';
import { UploadsService } from '@core/services/uploads/uploads.service';
import { QuoteNormalizerService } from './quote-normalizer.service';
import { CatalogService } from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  private readonly api = inject(ApiService);
  private readonly quoteNormalizeService = inject(QuoteNormalizerService);
  private readonly uploadsService = inject(UploadsService);
  private readonly catalogService = inject(CatalogService);

  async getQuotesList(): Promise<QuoteListItem[]> {
    const response = await this.api.get<IResponseHttp | unknown>(ROUTES.OPS_QUOTES_LIST);
    const payload = unwrapResponse<unknown>(response);
    const rows = Array.isArray(payload) ? payload : [];

    return rows
      .map((row) => this.mapQuoteListItem(row))
      .filter((row): row is QuoteListItem => row !== null);
  }

  async getOrCreateQuote(orderId: string): Promise<QuoteDraft> {
    const response = await this.api.post<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_GET_OR_CREATE(orderId),
      {},
    );
    return this.quoteNormalizeService.quoteDraft(unwrapResponse(response), orderId);
  }

  async updateQuoteItems(
    quoteId: string,
    items: { id: string; price: number; priceRuleId: string | null }[],
    globalPrice = false,
  ): Promise<QuoteUpdateResult> {
    if (items.length === 0) {
      return { updated: false, total: 0 };
    }

    const response = await this.api.patch<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_UPDATE_ITEMS(quoteId),
      {
        items,
        globalPrice,
      },
    );
    const payload = toObject(unwrapResponse(response));
    return {
      updated: readNullableBoolean(payload, 'updated') || false,
      total: readNumber(payload, 'total'),
    };
  }

  async sendQuote(quoteId: string): Promise<QuoteSendResult> {
    const response = await this.api.post<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_SEND(quoteId),
      {},
    );
    const payload = toObject(unwrapResponse(response));
    return {
      url: readString(payload, 'url'),
    };
  }

  async getQuoteByToken(token: string): Promise<PublicQuoteDetail> {
    const response = await this.api.get<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_PUBLIC_BY_TOKEN(token),
    );
    const payload = toObject(unwrapResponse(response));
    const quoteRaw = toObject(payload['quote']);
    const quote =
      quoteRaw && readString(quoteRaw, 'id')
        ? this.quoteNormalizeService.quoteDraft(quoteRaw)
        : null;

    return {
      vehicle: this.quoteNormalizeService.mapVehicle(payload['vehicle']),
      customer: this.quoteNormalizeService.mapClient(payload['customer']),
      quote,
    };
  }

  async approveQuote(quoteId: string, items: QuoteApprovalDecision[]): Promise<boolean> {
    await this.api.post<IResponseHttp | unknown>(ROUTES.OPS_QUOTES_APPROVE(quoteId), {
      items,
    });
    return true;
  }

  async createNewQuote(
    orderId: string,
    payload: CreateQuotePayload,
  ): Promise<QuoteDraft> {
    const response = await this.api.post<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_CREATE_NEW(orderId),
      payload,
    );
    return this.quoteNormalizeService.quoteDraft(unwrapResponse(response), orderId);
  }

  async getServiceCategories(): Promise<ServiceCategoryOption[]> {
    const response = await this.catalogService.getServiceCategories();
    const payload = unwrapResponse<unknown>({ payload: response });
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(toObject(payload)['payload'])
        ? (toObject(payload)['payload'] as unknown[])
        : [];

    return rows
      .map((row) => {
        const item = toObject(row);
        const id = readString(item, 'id');
        const name = readString(item, 'name');
        if (!id || !name) {
          return null;
        }
        return { id, name };
      })
      .filter((row): row is ServiceCategoryOption => Boolean(row));
  }

  async searchServicesByVehicleVersion(vehicleVersionId: string): Promise<QuoteItem[]> {
    const response = await this.api.get<IResponseHttp | unknown>(
      ROUTES.OPS_RECEPTION_SERVICES_SEARCH(vehicleVersionId),
    );
    const payload = unwrapResponse<unknown>(response);
    const rows = Array.isArray(payload) ? payload : [];
    return rows.map((row, index) =>
      this.quoteNormalizeService.mapCatalogServiceItem(row, index),
    );
  }

  async createCatalogService(payload: {
    categoryId: string;
    name: string;
    vehicleVersionId: string;
    price: string;
  }): Promise<ResponseCreateServiceQuote> {
    const response = await this.catalogService.createCatalogService(payload);
    return this.quoteNormalizeService.mapCatalogCreateService(unwrapResponse(response));
  }

  async addItemsToQuote(
    quoteId: string,
    payload: {
      price: number;
      priceRuleId: string;
    },
  ): Promise<boolean> {
    const response = await this.api.post<IResponseHttp>(
      ROUTES.OPS_QUOTES_ADD_ITEMS(quoteId),
      payload,
    );
    return response.payload?.added ?? false;
  }

  async removeItemsFromQuote(quoteId: string, itemIds: string): Promise<boolean> {
    const response = await this.api.post<IResponseHttp>(
      ROUTES.OPS_QUOTES_REMOVE_ITEMS(quoteId),
      { idQuoteItem: itemIds },
    );
    return response.payload?.removed ?? false;
  }

  async uploadApprovalEvidence(
    quoteId: string,
    orderId: string,
    file: File,
  ): Promise<void> {
    const extension = getFileExtension(file.name, file.type);
    const fileName = `quote-approval-${quoteId}-${Date.now()}.${extension}`;
    const presigned = await this.uploadsService.getPresignedUrls({
      orderId,
      type: EvidenceType.PROCESS,
      files: [{ fileName }],
    });

    const target = presigned[0];
    if (!target?.uploadUrl) {
      throw new Error('No se obtuvo URL firmada para la evidencia de aprobación.');
    }

    await this.uploadsService.uploadFile(target.uploadUrl, file);

    await this.api.patch<IResponseHttp | unknown>(
      ROUTES.OPS_QUOTES_ADD_APPROVAL_EVIDENCE(quoteId),
      {
        urls: [target.fileName],
      },
    );
  }

  private mapQuoteListItem(raw: unknown): QuoteListItem | null {
    const item = toObject(raw);
    const orderId = readNullableStringByKeys(item, ['orderId', 'id']);
    if (!orderId) {
      return null;
    }

    return {
      id: orderId,
      customer: {
        name: readString(item['customer'], 'name'),
        phone: readString(item['customer'], 'phone'),
      },
      vehicle: {
        plate: readString(item['vehicle'], 'plate'),
      },
      quote: readNullableStringByKeys(item, ['quote']) as QuoteListStatus,
      status: readString(item, 'status'),
    };
  }
}
